// src/services/invoices.service.js
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from "firebase/firestore";
import { db } from "./firebase/firebaseconfig";

class InvoicesService {
  constructor() {
    this.collectionName = "invoices";
  }

  // Generate invoice number
  generateInvoiceNumber() {
    const timestamp = Date.now().toString();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${timestamp.slice(-8)}${random}`;
  }

  // Calculate invoice totals
  calculateTotals(items, taxRate = 19, discount = 0) {
    const subtotal = items.reduce((sum, item) => 
      sum + (item.quantity * item.unitPrice), 0
    );
    const discountAmount = (subtotal * discount) / 100;
    const afterDiscount = subtotal - discountAmount;
    const taxAmount = (afterDiscount * taxRate) / 100;
    const total = afterDiscount + taxAmount;

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discountAmount: Math.round(discountAmount * 100) / 100,
      taxAmount: Math.round(taxAmount * 100) / 100,
      total: Math.round(total * 100) / 100
    };
  }

  // Get all invoices
  async getAllInvoices() {
    try {
      const invoicesRef = collection(db, this.collectionName);
      const q = query(invoicesRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);

      const invoices = [];
      querySnapshot.forEach((doc) => {
        invoices.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        });
      });

      return {
        success: true,
        invoices: invoices
      };
    } catch (error) {
      console.error("Get all invoices error:", error);
      return {
        success: false,
        error: error.message,
        invoices: []
      };
    }
  }

  // Get invoice by ID
  async getInvoiceById(invoiceId) {
    try {
      const invoiceRef = doc(db, this.collectionName, invoiceId);
      const invoiceSnap = await getDoc(invoiceRef);

      if (!invoiceSnap.exists()) {
        return {
          success: false,
          error: "Invoice not found",
          invoice: null
        };
      }

      const data = invoiceSnap.data();
      return {
        success: true,
        invoice: {
          id: invoiceSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        }
      };
    } catch (error) {
      console.error("Get invoice by ID error:", error);
      return {
        success: false,
        error: error.message,
        invoice: null
      };
    }
  }

  // Get invoices by status
  async getInvoicesByStatus(status) {
    try {
      const invoicesRef = collection(db, this.collectionName);
      const q = query(
        invoicesRef,
        where("status", "==", status),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);

      const invoices = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        invoices.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        });
      });

      return {
        success: true,
        invoices: invoices
      };
    } catch (error) {
      console.error("Get invoices by status error:", error);
      return {
        success: false,
        error: error.message,
        invoices: []
      };
    }
  }

  // Get invoices by client
  async getInvoicesByClient(clientName) {
    try {
      const invoicesRef = collection(db, this.collectionName);
      const q = query(
        invoicesRef,
        where("clientName", "==", clientName),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);

      const invoices = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        invoices.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        });
      });

      return {
        success: true,
        invoices: invoices
      };
    } catch (error) {
      console.error("Get invoices by client error:", error);
      return {
        success: false,
        error: error.message,
        invoices: []
      };
    }
  }

  // Create new invoice
  async createInvoice(invoiceData) {
    try {
      // Generate invoice number
      const invoiceNumber = this.generateInvoiceNumber();

      // Calculate totals
      const totals = this.calculateTotals(
        invoiceData.items,
        invoiceData.taxRate || 19,
        invoiceData.discount || 0
      );

      // Prepare invoice document
      const invoice = {
        invoiceNumber,
        clientName: invoiceData.clientName,
        clientEmail: invoiceData.clientEmail || '',
        clientPhone: invoiceData.clientPhone || '',
        clientAddress: invoiceData.clientAddress || '',
        clientWilaya: invoiceData.clientWilaya || '',
        invoiceDate: invoiceData.invoiceDate,
        dueDate: invoiceData.dueDate || '',
        items: invoiceData.items,
        taxRate: invoiceData.taxRate || 19,
        discount: invoiceData.discount || 0,
        notes: invoiceData.notes || '',
        status: invoiceData.status || 'draft',
        ...totals,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const invoicesRef = collection(db, this.collectionName);
      const docRef = await addDoc(invoicesRef, invoice);

      return {
        success: true,
        invoiceId: docRef.id,
        invoiceNumber: invoiceNumber,
        message: "Invoice created successfully"
      };
    } catch (error) {
      console.error("Create invoice error:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to create invoice"
      };
    }
  }

  // Update invoice
  async updateInvoice(invoiceId, updates) {
    try {
      // Recalculate totals if items changed
      let totals = {};
      if (updates.items) {
        totals = this.calculateTotals(
          updates.items,
          updates.taxRate || 19,
          updates.discount || 0
        );
      }

      const invoiceRef = doc(db, this.collectionName, invoiceId);
      await updateDoc(invoiceRef, {
        ...updates,
        ...totals,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        message: "Invoice updated successfully"
      };
    } catch (error) {
      console.error("Update invoice error:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to update invoice"
      };
    }
  }

  // Update invoice status
  async updateInvoiceStatus(invoiceId, status) {
    try {
      const invoiceRef = doc(db, this.collectionName, invoiceId);
      await updateDoc(invoiceRef, {
        status: status,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        message: "Invoice status updated successfully"
      };
    } catch (error) {
      console.error("Update invoice status error:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to update invoice status"
      };
    }
  }

  // Delete invoice
  async deleteInvoice(invoiceId) {
    try {
      const invoiceRef = doc(db, this.collectionName, invoiceId);
      await deleteDoc(invoiceRef);

      return {
        success: true,
        message: "Invoice deleted successfully"
      };
    } catch (error) {
      console.error("Delete invoice error:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to delete invoice"
      };
    }
  }

  // Get invoice statistics
  async getInvoiceStats() {
    try {
      const invoicesRef = collection(db, this.collectionName);
      const querySnapshot = await getDocs(invoicesRef);

      let stats = {
        total: 0,
        draft: 0,
        sent: 0,
        paid: 0,
        overdue: 0,
        totalAmount: 0,
        paidAmount: 0,
        unpaidAmount: 0
      };

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        stats.total++;
        stats[data.status]++;
        stats.totalAmount += data.total || 0;
        
        if (data.status === 'paid') {
          stats.paidAmount += data.total || 0;
        } else {
          stats.unpaidAmount += data.total || 0;
        }
      });

      return {
        success: true,
        stats: stats
      };
    } catch (error) {
      console.error("Get invoice stats error:", error);
      return {
        success: false,
        error: error.message,
        stats: null
      };
    }
  }

  // Search invoices
  async searchInvoices(searchTerm) {
    try {
      const invoicesRef = collection(db, this.collectionName);
      const querySnapshot = await getDocs(invoicesRef);

      const invoices = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const searchLower = searchTerm.toLowerCase();

        if (
          data.invoiceNumber?.toLowerCase().includes(searchLower) ||
          data.clientName?.toLowerCase().includes(searchLower) ||
          data.clientEmail?.toLowerCase().includes(searchLower)
        ) {
          invoices.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          });
        }
      });

      return {
        success: true,
        invoices: invoices
      };
    } catch (error) {
      console.error("Search invoices error:", error);
      return {
        success: false,
        error: error.message,
        invoices: []
      };
    }
  }

  // Get overdue invoices
  async getOverdueInvoices() {
    try {
      const today = new Date().toISOString().split('T')[0];
      const invoicesRef = collection(db, this.collectionName);
      const querySnapshot = await getDocs(invoicesRef);

      const invoices = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (
          data.dueDate &&
          data.dueDate < today &&
          data.status !== 'paid'
        ) {
          invoices.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          });
        }
      });

      return {
        success: true,
        invoices: invoices
      };
    } catch (error) {
      console.error("Get overdue invoices error:", error);
      return {
        success: false,
        error: error.message,
        invoices: []
      };
    }
  }
}

export default new InvoicesService();